import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import type { Announcements, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { Rows3 } from 'lucide-react';
import { useDndSensors } from '@/hooks/useDndSensors';
import { useLeadsBoard } from '@/hooks/useLeadsBoard';
import { BoardColumn } from '@/components/leads/board/BoardColumn';
import { BoardCardPreview } from '@/components/leads/board/BoardCardPreview';
import type { TBoardLead } from '@/@types/leadsBoard';

export function LeadsBoardPage() {
  const { columns, stages, isLoading, moveLead } = useLeadsBoard();
  const sensors = useDndSensors();
  const [activeLead, setActiveLead] = useState<TBoardLead | null>(null);

  const findLead = useCallback(
    (leadId: string | number) =>
      columns.flatMap(column => column.leads).find(lead => lead._id === leadId),
    [columns]
  );

  const findStageLabel = useCallback(
    (status: string | number) =>
      columns.find(column => column.stage.status === status)?.stage.label,
    [columns]
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveLead(findLead(event.active.id) ?? null);
    },
    [findLead]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveLead(null);
      const { active, over } = event;
      if (!over) return;

      const lead = findLead(active.id);
      const targetStage = columns.find(column => column.stage.status === over.id)?.stage;
      if (!lead || !targetStage || lead.status === targetStage.status) return;

      moveLead(lead._id, targetStage.status);

      // The card remounts under a different column's parent element once the
      // move commits, so a keyboard user's focus falls back to <body>.
      // Wait a frame for that remount, then refocus the same card by id.
      requestAnimationFrame(() => {
        const node = document.querySelector(`[data-lead-id="${lead._id}"]`);
        if (node instanceof HTMLElement) node.focus();
      });
    },
    [columns, findLead, moveLead]
  );

  const announcements: Announcements = useMemo(
    () => ({
      onDragStart({ active }) {
        const lead = findLead(active.id);
        return lead ? `Picked up ${lead.name ?? 'Unknown'}.` : undefined;
      },
      onDragOver({ active, over }) {
        const lead = findLead(active.id);
        const stageLabel = over ? findStageLabel(over.id) : undefined;
        if (!lead || !stageLabel) return undefined;
        return `${lead.name ?? 'Unknown'} is over the ${stageLabel} column.`;
      },
      onDragEnd({ active, over }) {
        const lead = findLead(active.id);
        if (!lead) return undefined;
        const stageLabel = over ? findStageLabel(over.id) : undefined;
        return stageLabel
          ? `${lead.name ?? 'Unknown'} was moved to the ${stageLabel} column.`
          : `${lead.name ?? 'Unknown'} was returned to its original position.`;
      },
      onDragCancel({ active }) {
        const lead = findLead(active.id);
        return lead ? `Moving ${lead.name ?? 'Unknown'} was cancelled.` : undefined;
      },
    }),
    [findLead, findStageLabel]
  );

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10" aria-busy={isLoading}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-[28px] font-bold text-foreground">Leads Board</h1>
        <Link
          to="/leads"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Rows3 className="w-4 h-4" />
          Table View
        </Link>
      </div>

      {isLoading ? (
        <>
          <p role="status" className="sr-only">
            Loading leads board…
          </p>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {stages.map(stage => (
              <div
                key={stage.status}
                className="w-72 h-96 shrink-0 rounded-xl bg-muted/40 animate-pulse"
              />
            ))}
          </div>
        </>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          accessibility={{ announcements }}
        >
          <div
            tabIndex={0}
            aria-label="Leads board columns, scroll horizontally"
            className="flex gap-4 overflow-x-auto pb-4"
          >
            {columns.map(({ stage, leads }) => (
              <BoardColumn key={stage.status} stage={stage} leads={leads} />
            ))}
          </div>
          <DragOverlay>
            {activeLead && (
              <div className="shadow-lg cursor-grabbing">
                <BoardCardPreview lead={activeLead} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
